import React, { useState } from 'react';
import { Scroll } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getFrameMechanics, getFrameConfig } from '../../config/campaignFrameConfig';
import CampaignFrameRenderer from '../campaign-frames/CampaignFrameRenderer';
import Modal from '../common/Modal';
import useFrameSession from '../../hooks/useFrameSession';
import { useDaggerheartData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';

/**
 * CampaignFrameMechanicsPanel - GM Dashboard panel for campaign frame mechanics.
 *
 * Renders mechanic buttons that open full-screen modals (matching the character
 * sheet pattern). Each modal renders a CampaignFrameRenderer with the appropriate
 * pattern component.
 */
const CampaignFrameMechanicsPanel = ({ campaign, characters = [] }) => {
  const frameId = campaign?.campaignFrame;
  const allData = useDaggerheartData();
  const { user } = useAuth();
  const { sessionState, updateSessionState } = useFrameSession(frameId || 'core');

  const [activeMechanic, setActiveMechanic] = useState(null);

  // No frame or core rules — show placeholder
  if (!frameId || frameId === 'core') {
    return (
      <div className="text-center py-6 text-gray-500">
        <Scroll className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No campaign frame selected</p>
        <p className="text-xs mt-1">Set a frame in Campaign Settings</p>
      </div>
    );
  }

  const frameConfig = getFrameConfig(frameId);
  const mechanics = getFrameMechanics(frameId);
  const mechanicIds = Object.keys(mechanics);

  if (mechanicIds.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <Scroll className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-sm">No mechanics configured</p>
        <p className="text-xs mt-1">Frame: {frameConfig?.name || frameId}</p>
      </div>
    );
  }

  // Use first character as proxy for GM-level mechanics.
  // Provide a real updateCharacter that persists to Firestore.
  const proxyCharacter = characters[0] || {};
  const updateProxyCharacter = async (updatedCharacter) => {
    if (!proxyCharacter.id) return;
    try {
      const charRef = doc(db, 'characters', proxyCharacter.id);
      const { id, ...updates } = updatedCharacter;
      await updateDoc(charRef, updates);
    } catch (err) {
      console.error('CampaignFrameMechanicsPanel: Failed to update character', err);
    }
  };

  const activeMechanicConfig = activeMechanic ? mechanics[activeMechanic] : null;
  const activeMechanicLabel = activeMechanicConfig?.displayName
    || (activeMechanic || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <>
      {/* Mechanic Buttons */}
      <div className="grid grid-cols-2 gap-2">
        {mechanicIds.map(mechanicId => {
          const mechanic = mechanics[mechanicId];
          const label = mechanic.displayName
            || mechanicId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

          return (
            <button
              key={mechanicId}
              onClick={() => setActiveMechanic(mechanicId)}
              className="py-3 px-4 bg-indigo-800 text-indigo-200 rounded-lg hover:bg-indigo-700 hover:text-white border border-indigo-600 transition-all duration-200 font-medium text-sm"
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Full-screen Modal */}
      {activeMechanic && (
        <Modal
          title={activeMechanicLabel}
          onClose={() => setActiveMechanic(null)}
        >
          <CampaignFrameRenderer
            frameId={frameId}
            mechanicId={activeMechanic}
            character={proxyCharacter}
            updateCharacter={updateProxyCharacter}
            allData={allData}
            sessionState={sessionState}
            setSessionState={updateSessionState}
            onClose={() => setActiveMechanic(null)}
            campaignId={campaign?.id}
            user={user}
          />
        </Modal>
      )}
    </>
  );
};

export default CampaignFrameMechanicsPanel;
